import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  vipUsersAll,
  vipBenefits,
  vipScoreActions,
  vipDashboardStats,
  vipAccessMethods,
  vipVisibility,
  tierConfig,
  getDestacadosVips,
  getMasActivosVips,
  formatVipCount,
  type VipUser,
} from '@/mocks/rfwVipUsers';

function VipCard({ user, size = 'md' }: { user: VipUser; size?: 'lg' | 'md' | 'sm' }) {
  const t = tierConfig[user.tier];

  if (size === 'sm') {
    return (
      <div className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-background-300/70 transition-all duration-200">
        <div className="relative shrink-0">
          <img src={user.photoUrl} alt={user.name} className="w-10 h-10 rounded-full object-cover object-top" />
          <span className={`absolute -bottom-1 -right-1 text-xs ${t.emoji}`}></span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-foreground-950 truncate">{user.name}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className={`text-[9px] font-medium ${t.textColor}`}>{t.emoji} {t.label}</span>
            <span className="text-[9px] text-foreground-400">{user.radioFrequencia}</span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm font-bold text-foreground-950">{user.score}</p>
          <p className="text-[9px] text-foreground-400">pts VIP</p>
        </div>
      </div>
    );
  }

  if (size === 'lg') {
    const rank = vipUsersAll.findIndex((u) => u.id === user.id) + 1;
    return (
      <div className="rounded-2xl border overflow-hidden transition-all duration-300 hover:border-violet-200/60 bg-background-50 border-background-200/70">
        <div className="p-5 text-center">
          <div className="relative inline-block mb-3">
            <img src={user.photoUrl} alt={user.name} className={`w-20 h-20 rounded-2xl object-cover object-top border-2 ${t.borderColor}`} />
            <div className={`absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-extrabold text-background-50 bg-gradient-to-r ${t.gradient}`}>
              #{rank}
            </div>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-0.5">{user.name}</p>
          <p className="text-[10px] text-foreground-500 mb-2">{user.username} · {user.radioFrequencia}</p>
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold ${t.badgeBg} ${t.badgeText}`}>
            {t.emoji} {t.label}
          </span>
          <div className="mt-3 text-center">
            <p className={`text-xl font-extrabold ${t.color}`}>{user.score}</p>
            <p className="text-[9px] text-foreground-400">puntos VIP</p>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-1 text-center">
            <div>
              <p className="text-[10px] font-bold text-foreground-950">{user.puntosTotales.toLocaleString()}</p>
              <p className="text-[8px] text-foreground-400">Pts Totales</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-foreground-950">{user.actividadDias}d</p>
              <p className="text-[8px] text-foreground-400">Actividad</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-foreground-950">{user.invitacionesHechas}</p>
              <p className="text-[8px] text-foreground-400">Invit.</p>
            </div>
          </div>
          {/* Access methods mini pills */}
          <div className="mt-3 flex flex-wrap justify-center gap-1">
            {user.accessMethods.map((am) => {
              const method = vipAccessMethods.find((m) => m.type === am);
              if (!method) return null;
              return (
                <span key={am} className={`px-1.5 py-0.5 rounded-full text-[8px] font-semibold ${method.bgColor} ${method.color}`}>
                  <i className={`${method.icon} mr-0.5 text-[7px]`}></i>
                  {method.label.split(' ')[0]}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 p-3.5 rounded-xl bg-background-50 border border-background-200/70 hover:border-background-300/70 transition-all duration-200">
      <div className="relative shrink-0">
        <img src={user.photoUrl} alt={user.name} className="w-11 h-11 rounded-xl object-cover object-top" />
        <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold bg-gradient-to-r ${t.gradient} text-background-50`}>
          <i className="ri-vip-diamond-line text-[7px]"></i>
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="text-xs font-semibold text-foreground-950 truncate">{user.name}</p>
          <span className={`text-[9px] font-medium ${t.textColor}`}>{t.emoji}</span>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[9px] text-foreground-500">{user.radioFrequencia}</span>
          <span className="text-[9px] text-foreground-400">· {user.actividadDias}d · +{user.scoreAdicionalGenerado} score</span>
        </div>
      </div>
      <div className="text-right shrink-0">
        <p className={`text-sm font-bold ${t.color}`}>{user.score}</p>
      </div>
    </div>
  );
}

function BenefitCard({ benefit }: { benefit: typeof vipBenefits[0] }) {
  return (
    <div className="p-5 rounded-2xl bg-background-50 border border-background-200/70 text-center">
      <div className={`w-12 h-12 mx-auto mb-3 rounded-2xl flex items-center justify-center ${benefit.bgColor}`}>
        <i className={`${benefit.icon} ${benefit.color} text-xl`}></i>
      </div>
      <h4 className="text-sm font-bold text-foreground-950 mb-2">{benefit.label}</h4>
      <p className="text-xs text-foreground-500 leading-relaxed mb-3">{benefit.description}</p>
      <div className="flex items-center justify-center gap-1.5">
        {benefit.tiers.map((tier) => {
          const tc = tierConfig[tier];
          return (
            <span key={tier} className={`px-2 py-0.5 rounded-full text-[9px] font-semibold ${tc.badgeBg} ${tc.badgeText}`}>
              {tc.emoji}
            </span>
          );
        })}
      </div>
    </div>
  );
}

export default function VipClubSection() {
  const [activeTab, setActiveTab] = useState<'destacados' | 'activos' | 'beneficios'>('destacados');
  const destacados = getDestacadosVips(6);
  const activos = getMasActivosVips(9);

  const tabs = [
    { id: 'destacados' as const, label: 'VIP Destacados', icon: 'ri-star-line' },
    { id: 'activos' as const, label: 'VIP Más Activos', icon: 'ri-fire-line' },
    { id: 'beneficios' as const, label: 'Beneficios', icon: 'ri-gift-2-line' },
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-violet-500 animate-pulse"></span>
            CLUB VIP RFW
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Premiamos a los Más <span className="text-violet-600">Comprometidos</span>
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Insignia VIP, contenido exclusivo, eventos privados, concursos premium y acceso anticipado. VIP debe sentirse especial.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: 'VIPs Totales', value: vipDashboardStats.totalVips.toString(), icon: 'ri-vip-diamond-line', color: 'text-violet-600 bg-violet-50' },
            { label: 'VIP Diamante', value: vipDashboardStats.diamante.toString(), icon: 'ri-vip-crown-fill', color: 'text-fuchsia-600 bg-fuchsia-50' },
            { label: 'VIP Oro', value: vipDashboardStats.oro.toString(), icon: 'ri-vip-diamond-fill', color: 'text-amber-600 bg-amber-50' },
            { label: 'Score Generado', value: formatVipCount(vipDashboardStats.scoreTotalGenerado), icon: 'ri-flashlight-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Suscriptores', value: vipDashboardStats.suscriptores.toString(), icon: 'ri-vip-crown-line', color: 'text-rose-600 bg-rose-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex bg-background-100 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-violet-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'destacados' && (
          <div className="space-y-6">
            {/* Podium Top 3 */}
            <div className="flex items-end justify-center gap-3 md:gap-5 px-2 mb-8">
              {destacados[1] && <VipCard user={destacados[1]} size="lg" />}
              {destacados[0] && <VipCard user={destacados[0]} size="lg" />}
              {destacados[2] && <VipCard user={destacados[2]} size="lg" />}
            </div>

            {/* Rest 4-6 */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-list-check text-xs text-foreground-500"></i>
                </div>
                <p className="text-xs font-bold text-foreground-700 uppercase tracking-wider">Más VIP Destacados</p>
              </div>
              {destacados.slice(3, 6).map((user) => (
                <VipCard key={user.id} user={user} size="sm" />
              ))}
            </div>

            {/* Tiers Overview */}
            <div className="mt-6 p-5 rounded-2xl bg-background-50 border border-background-200/70">
              <h4 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-4">
                <i className="ri-stack-line text-violet-500"></i>
                Niveles VIP
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {(['diamante', 'oro', 'plata'] as const).map((tier) => {
                  const tc = tierConfig[tier];
                  const count = tier === 'diamante' ? vipDashboardStats.diamante : tier === 'oro' ? vipDashboardStats.oro : vipDashboardStats.plata;
                  return (
                    <div key={tier} className="p-4 rounded-xl bg-background-100 text-center">
                      <div className={`w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center ${tc.badgeBg}`}>
                        <span className="text-lg">{tc.emoji}</span>
                      </div>
                      <p className={`text-xs font-bold ${tc.textColor} mb-0.5`}>{tc.label}</p>
                      <p className="text-[10px] text-foreground-500">{count} miembros</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'activos' && (
          <div className="space-y-6">
            {/* Grid de Más Activos */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {activos.slice(0, 9).map((user) => (
                <div key={user.id} className="p-4 rounded-2xl bg-background-50 border border-background-200/70 hover:border-violet-200/50 transition-all duration-200">
                  <div className="flex items-center gap-3 mb-3">
                    <img src={user.photoUrl} alt={user.name} className="w-12 h-12 rounded-xl object-cover object-top" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-foreground-950 truncate">{user.name}</p>
                      <p className="text-[10px] text-foreground-500">{user.username}</p>
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-semibold mt-0.5 ${tierConfig[user.tier].badgeBg} ${tierConfig[user.tier].badgeText}`}>
                        {tierConfig[user.tier].emoji} {tierConfig[user.tier].label}
                      </span>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-bold text-violet-600">{user.actividadDias}</p>
                      <p className="text-[8px] text-foreground-400">días</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="p-2 rounded-lg bg-background-100">
                      <p className="text-xs font-bold text-foreground-950">{user.puntosTotales.toLocaleString()}</p>
                      <p className="text-[8px] text-foreground-400">Puntos</p>
                    </div>
                    <div className="p-2 rounded-lg bg-background-100">
                      <p className="text-xs font-bold text-foreground-950">+{user.scoreAdicionalGenerado}</p>
                      <p className="text-[8px] text-foreground-400">Score</p>
                    </div>
                    <div className="p-2 rounded-lg bg-background-100">
                      <p className="text-xs font-bold text-foreground-950">{user.streak}d</p>
                      <p className="text-[8px] text-foreground-400">Racha</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'beneficios' && (
          <div className="space-y-6">
            {/* Benefits Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {vipBenefits.map((benefit) => (
                <BenefitCard key={benefit.key} benefit={benefit} />
              ))}
            </div>

            {/* Visibility */}
            <div className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
              <h4 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-4">
                <i className="ri-eye-line text-violet-500"></i>
                Visibilidad VIP en la Plataforma
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {vipVisibility.map((v) => (
                  <div key={v.key} className="p-3 rounded-xl bg-background-100 text-center">
                    <div className={`w-9 h-9 mx-auto mb-2 rounded-full flex items-center justify-center ${v.bgColor}`}>
                      <i className={`${v.icon} ${v.color} text-sm`}></i>
                    </div>
                    <p className="text-[10px] font-bold text-foreground-950 mb-0.5">{v.label}</p>
                    <p className="text-[8px] text-foreground-500">{v.location}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Score Actions */}
            <div className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
              <h4 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-4">
                <i className="ri-flashlight-line text-violet-500"></i>
                Los VIP Generan Score Adicional
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {vipScoreActions.map((action) => (
                  <div key={action.key} className={`flex items-center gap-2.5 p-3 rounded-xl ${action.bgColor}`}>
                    <i className={`${action.icon} ${action.color} text-sm`}></i>
                    <div>
                      <p className="text-[10px] font-bold text-foreground-950">+{action.points} pts</p>
                      <p className="text-[9px] text-foreground-500">{action.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Access Methods */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {vipAccessMethods.map((method) => (
                <div key={method.type} className="p-4 rounded-2xl bg-background-50 border border-background-200/70 text-center">
                  <div className={`w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center ${method.bgColor}`}>
                    <i className={`${method.icon} ${method.color} text-base`}></i>
                  </div>
                  <p className="text-xs font-bold text-foreground-950 mb-1">{method.label}</p>
                  <p className="text-[10px] text-foreground-500 leading-relaxed mb-2">{method.description}</p>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-semibold ${method.bgColor} ${method.color}`}>
                    {method.threshold}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-10">
          <Link
            to="/vip-rfw"
            className="inline-flex items-center gap-2 px-6 py-3 bg-violet-500 hover:bg-violet-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-vip-diamond-line"></i>
            Ver Club VIP Completo
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Regla Final */}
        <div className="mt-10 p-6 rounded-2xl bg-violet-100/70 border border-violet-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-violet-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-vip-diamond-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            VIP debe sentirse especial.
          </p>
          <p className="text-xs text-foreground-600 max-w-lg mx-auto">
            No se trata solo de una insignia — es una experiencia completa. Desde la visibilidad en toda la plataforma hasta contenido que nadie más ve, eventos a los que solo ellos asisten y concursos con premios premium. Ser VIP en RFW es pertenecer al círculo más exclusivo de la radio digital.
          </p>
        </div>
      </div>
    </section>
  );
}